import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CREATIVE_SANDBOX_URL } from '../../../../config.ts';
import { SANDBOX_LOAD_TIMEOUT_MS } from './constants.ts';
import { parseSandboxMessage } from './utils.ts';

type DetectedDimensions = { width: number; height: number };

type UseCreativeSandboxOptions = {
  html: string | null | undefined;
  onDimensionsDetected?: (dimensions: DetectedDimensions) => void;
};

/**
 * Manages a sandboxed iframe for rendering untrusted ad creative HTML.
 *
 * The iframe loads a shim page from a separate origin so creative scripts
 * cannot access the main application's cookies, storage or DOM. Content is
 * delivered via `postMessage` after the shim is ready.
 */
export const useCreativeSandbox = ({ html, onDimensionsDetected }: UseCreativeSandboxOptions) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isShimReady = useRef(false);
  const queuedMarkup = useRef<string | null>(null);
  // Stable ref so the message listener never needs to re-register when the parent re-renders
  const onDimensionsDetectedRef = useRef(onDimensionsDetected);
  onDimensionsDetectedRef.current = onDimensionsDetected;
  // The shim can emit multiple 'resize' events; only the first one counts
  const hasDimensionsBeenReported = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const loadingTimeoutRefId = useRef<ReturnType<typeof setTimeout>>(undefined);

  const clearLoadingTimeout = useCallback(() => {
    if (loadingTimeoutRefId.current) {
      clearTimeout(loadingTimeoutRefId.current);
      loadingTimeoutRefId.current = undefined;
    }
  }, []);

  const sandboxOrigin = useMemo(() => new URL(CREATIVE_SANDBOX_URL).origin, []);

  const sendToSandbox = useCallback(
    (markup: string) => {
      const iframe = iframeRef.current;

      if (!iframe?.contentWindow) return;

      setIsLoading(true);
      setIsError(false);
      hasDimensionsBeenReported.current = false;

      clearLoadingTimeout();
      loadingTimeoutRefId.current = setTimeout(() => {
        setIsLoading(false);
        setIsError(true);
      }, SANDBOX_LOAD_TIMEOUT_MS);

      iframe.contentWindow.postMessage(JSON.stringify({ type: 'render', html: markup }), sandboxOrigin);
    },
    [clearLoadingTimeout, sandboxOrigin]
  );

  const handleIframeLoad = useCallback(() => {
    isShimReady.current = true;

    if (queuedMarkup.current !== null) {
      sendToSandbox(queuedMarkup.current);
      queuedMarkup.current = null;
    }
  }, [sendToSandbox]);

  useEffect(() => {
    const handleShimMessage = (event: MessageEvent) => {
      if (event.origin !== sandboxOrigin) return;

      // Ignore messages from other sandbox instances on the same page (e.g. the fullscreen modal)
      if (!iframeRef.current?.contentWindow || event.source !== iframeRef.current.contentWindow) return;

      const msg = parseSandboxMessage(event.data);
      if (!msg) return;

      switch (msg.type) {
        case 'loading':
          // sendToSandbox already set isLoading=true and started the safety timer;
          // acting on unsolicited 'loading' messages would create a stuck isLoading state.
          break;
        case 'loaded':
          clearLoadingTimeout();
          setIsLoading(false);
          setIsError(false);
          break;
        case 'error':
          clearLoadingTimeout();
          setIsLoading(false);
          setIsError(true);
          break;
        case 'resize':
          if (hasDimensionsBeenReported.current) break;
          // Treat very small content (tracking pixels, hidden elements) as an error
          if (msg.width <= 2 || msg.height <= 2) {
            hasDimensionsBeenReported.current = true;
            onDimensionsDetectedRef.current?.({ width: 0, height: 0 });
            clearLoadingTimeout();
            setIsLoading(false);
            setIsError(true);
          } else {
            hasDimensionsBeenReported.current = true;
            onDimensionsDetectedRef.current?.({ width: msg.width, height: msg.height });
          }
          break;
      }
    };

    window.addEventListener('message', handleShimMessage);

    return () => window.removeEventListener('message', handleShimMessage);
  }, [sandboxOrigin, clearLoadingTimeout]);

  // Clear any pending timeout if the component unmounts while a load cycle is in progress
  useEffect(() => () => clearLoadingTimeout(), [clearLoadingTimeout]);

  useEffect(() => {
    if (html == null) return;

    if (isShimReady.current) {
      sendToSandbox(html);
    } else {
      queuedMarkup.current = html;
    }
  }, [html, sendToSandbox]);

  return {
    iframeRef,
    iframeSrc: CREATIVE_SANDBOX_URL,
    isLoading,
    isError,
    onLoad: handleIframeLoad,
  };
};
